import React, { useMemo } from "react";
import { T } from "./theme";
import {
  ACTIVITY_OPTIONS,
  bmrFor,
  chartEndpointsForPlan,
  dailyTargetFor,
  effectivePaceLbsPerWeek,
  projectedEndDateYmd,
  tdeeFor,
} from "./calc";
import { displayWeight } from "./units";
import type { GoalDirection, OnboardingDraft } from "./types";

const Eyebrow = ({ children, color = T.muted, sz = "0.7rem" }: { children: React.ReactNode; color?: string; sz?: string }) => (
  <div style={{ fontSize: sz, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color }}>
    {children}
  </div>
);

const StatTile = ({ label, value, suffix, accent }: { label: string; value: string; suffix?: string; accent?: string }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.025)",
      border: `1px solid ${T.hairline}`,
      borderRadius: 12,
      padding: "12px 14px",
    }}
  >
    <Eyebrow sz="0.62rem">{label}</Eyebrow>
    <div
      style={{
        fontSize: "1.18rem",
        fontWeight: 800,
        ...T.tabular,
        marginTop: 4,
        letterSpacing: "-0.01em",
        color: accent ?? T.fg,
      }}
    >
      {value}
      {suffix ? (
        <span style={{ fontSize: "0.66rem", color: T.muted, fontWeight: 700, marginLeft: 4 }}>{suffix}</span>
      ) : null}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Preview cards by step. Each card derives its values from the current draft.
// ─────────────────────────────────────────────────────────────────────────

const GOAL_LABEL: Record<GoalDirection, string> = {
  lose: "Lose weight",
  maintain: "Maintain",
  gain: "Build muscle",
};

export function PreviewIntro({ picked }: { picked?: GoalDirection | null }): React.ReactElement {
  const pickedLabel = picked ? GOAL_LABEL[picked] : null;
  return (
    <>
      <Eyebrow>What you'll get</Eyebrow>
      <div
        style={{
          background: T.bgCard,
          border: `1px solid ${T.hairline}`,
          borderRadius: 18,
          padding: "20px 22px",
          color: T.fg2,
          fontSize: "0.9rem",
          lineHeight: 1.55,
        }}
      >
        We'll plan a focused <span style={{ color: T.fg, fontWeight: 700 }}>sprint</span> with a weekly target, an
        adjusting calorie budget, and a check-in cadence — same physics the rest of the app uses.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <StatTile label="Sprint" value="6–12" suffix="wk" />
        <StatTile label="Adjusts" value="Weekly" />
        <StatTile label="Effort" value="Low" />
      </div>
      <div
        style={{
          marginTop: "auto",
          padding: "14px 16px",
          background: "rgba(255,255,255,0.025)",
          border: `1px solid ${T.hairline}`,
          borderRadius: 12,
        }}
      >
        <Eyebrow>{pickedLabel ? `Picked: ${pickedLabel}` : "Pick a direction"}</Eyebrow>
        <div style={{ color: T.fg2, fontSize: "0.82rem", marginTop: 6, lineHeight: 1.5 }}>
          {pickedLabel
            ? "Next: tell us about your body so we can calibrate burn."
            : "We use it to size the sprint and set your daily target."}
        </div>
      </div>
    </>
  );
}

export function PreviewBMR({ draft }: { draft: OnboardingDraft }): React.ReactElement {
  const bmr = useMemo(() => {
    if (!draft.sex || !draft.birthday) return null;
    return bmrFor({ weightLbs: draft.currentWeightLbs, heightIn: draft.heightIn, sex: draft.sex, birthday: draft.birthday });
  }, [draft.sex, draft.birthday, draft.heightIn, draft.currentWeightLbs]);

  return (
    <>
      <Eyebrow>Resting burn — live</Eyebrow>
      <div
        style={{
          background: T.bgCard,
          border: `1px solid ${T.hairline}`,
          borderRadius: 22,
          padding: "26px 24px",
        }}
      >
        <Eyebrow>BMR · Mifflin–St Jeor</Eyebrow>
        <div
          style={{
            marginTop: 8,
            fontSize: "2.8rem",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            color: T.fg,
            ...T.tabular,
            lineHeight: 1,
          }}
        >
          {bmr ? Math.round(bmr).toLocaleString() : "—"}
          <span style={{ fontSize: "0.9rem", color: T.muted, marginLeft: 8, fontWeight: 700 }}>kcal/day</span>
        </div>
        <div style={{ marginTop: 6, color: T.fg2, fontSize: "0.84rem", lineHeight: 1.5 }}>
          What you burn at rest. Activity multiplier comes next.
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <StatTile label="Sex" value={draft.sex === "M" ? "Male" : draft.sex === "F" ? "Female" : draft.sex === "X" ? "—" : "—"} />
        <StatTile
          label="Age"
          value={(() => {
            if (!draft.birthday) return "—";
            const b = new Date(draft.birthday + "T00:00:00");
            const today = new Date();
            let a = today.getFullYear() - b.getFullYear();
            const md = today.getMonth() - b.getMonth();
            if (md < 0 || (md === 0 && today.getDate() < b.getDate())) a -= 1;
            return String(a);
          })()}
        />
        <StatTile
          label="Height"
          value={(() => {
            const ft = Math.floor(draft.heightIn / 12);
            const inch = Math.round((draft.heightIn - ft * 12) * 10) / 10;
            return draft.unitsHeight === "in" ? `${ft}' ${inch}"` : `${Math.round(draft.heightIn * 2.54)} cm`;
          })()}
        />
      </div>
      <div
        style={{
          marginTop: "auto",
          background: "rgba(59,130,246,0.05)",
          border: "1px solid rgba(59,130,246,0.30)",
          borderRadius: 12,
          padding: "12px 14px",
        }}
      >
        <Eyebrow color="#93c5fd">Privacy</Eyebrow>
        <div style={{ color: T.fg2, fontSize: "0.78rem", lineHeight: 1.5, marginTop: 4 }}>
          Body data stays on this device until you sign in. We never sell or share it.
        </div>
      </div>
    </>
  );
}

export function PreviewTDEE({ draft }: { draft: OnboardingDraft }): React.ReactElement {
  const { bmr, tdee, factor } = useMemo(() => {
    if (!draft.sex || !draft.birthday) return { bmr: null, tdee: null, factor: draft.activityFactor };
    const b = bmrFor({ weightLbs: draft.currentWeightLbs, heightIn: draft.heightIn, sex: draft.sex, birthday: draft.birthday });
    const t = b ? tdeeFor(b, draft.activityFactor) : null;
    return { bmr: b, tdee: t, factor: draft.activityFactor };
  }, [draft]);

  const activeKcal = bmr && tdee ? Math.max(0, Math.round(tdee - bmr * 1.05)) : 0;
  const neatKcal = bmr ? Math.round(bmr * 0.05) : 0;
  const restingKcal = bmr ? Math.round(bmr) : 0;
  const totalKcal = tdee ? Math.round(tdee) : 0;
  const restingPct = totalKcal ? (restingKcal / totalKcal) * 100 : 0;
  const activePct = totalKcal ? (activeKcal / totalKcal) * 100 : 0;
  const neatPct = totalKcal ? (neatKcal / totalKcal) * 100 : 0;

  return (
    <>
      <Eyebrow>Daily burn — live</Eyebrow>
      <div
        style={{
          background: T.bgCard,
          border: `1px solid ${T.hairline}`,
          borderRadius: 22,
          padding: "26px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <Eyebrow>TDEE</Eyebrow>
          <span style={{ fontSize: "0.7rem", color: T.muted, fontWeight: 700 }}>BMR × {factor.toFixed(2)}</span>
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: "2.8rem",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            color: T.fg,
            ...T.tabular,
            lineHeight: 1,
          }}
        >
          {totalKcal ? totalKcal.toLocaleString() : "—"}
          <span style={{ fontSize: "0.9rem", color: T.muted, marginLeft: 8, fontWeight: 700 }}>kcal/day</span>
        </div>
        <div style={{ marginTop: 6, color: T.fg2, fontSize: "0.84rem", lineHeight: 1.5 }}>
          Total burn including training and movement.
        </div>
      </div>
      <div
        style={{
          background: T.bgCard,
          border: `1px solid ${T.hairline}`,
          borderRadius: 14,
          padding: "16px 18px",
        }}
      >
        <Eyebrow>Breakdown</Eyebrow>
        <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginTop: 12 }}>
          <div style={{ width: `${restingPct}%`, background: T.blue }} />
          <div style={{ width: `${activePct}%`, background: T.lime }} />
          <div style={{ width: `${neatPct}%`, background: "rgba(255,255,255,0.30)" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
          <Legend dot={T.blue} label="Resting" v={restingKcal.toLocaleString()} />
          <Legend dot={T.lime} label="Activity" v={activeKcal.toLocaleString()} />
          <Legend dot="rgba(255,255,255,0.6)" label="NEAT" v={neatKcal.toLocaleString()} />
        </div>
      </div>
    </>
  );
}

function Legend({ dot, label, v }: { dot: string; label: string; v: string }): React.ReactElement {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: dot }} />
        <span style={{ fontSize: "0.66rem", color: T.muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: "0.92rem", fontWeight: 800, ...T.tabular, marginTop: 4, letterSpacing: "-0.01em" }}>{v}</div>
    </div>
  );
}

export function PreviewProjection({ draft, goal }: { draft: OnboardingDraft; goal: GoalDirection }): React.ReactElement {
  const { bmr, tdee, dailyTarget, endYmd } = useMemo(() => {
    const sex = draft.sex ?? "X";
    const b = bmrFor({ weightLbs: draft.currentWeightLbs, heightIn: draft.heightIn, sex, birthday: draft.birthday });
    const t = b ? tdeeFor(b, draft.activityFactor) : null;
    const target = t ? dailyTargetFor({ goal, paceLbsPerWeek: draft.paceLbsPerWeekAbs, tdee: t, sex }) : 0;
    // Use effective pace (post-floor) so the projected end date matches the
    // intake-derived "Plan" line on the dashboard.
    const effPace = t && t > 0
      ? effectivePaceLbsPerWeek({ paceLbsPerWeekAbs: draft.paceLbsPerWeekAbs, tdee: t, sex, goal })
      : draft.paceLbsPerWeekAbs;
    const end =
      goal === "maintain"
        ? ""
        : projectedEndDateYmd(draft.currentWeightLbs, draft.targetWeightLbs, effPace);
    return { bmr: b, tdee: t, dailyTarget: target, endYmd: end };
  }, [draft, goal]);

  const { y0, y1 } = chartEndpointsForPlan(goal, draft.paceLbsPerWeekAbs);
  const sign = draft.targetWeightLbs > draft.currentWeightLbs ? "+" : "−";

  return (
    <>
      <Eyebrow>Projection — live</Eyebrow>
      <div
        style={{
          background: T.bgCard,
          border: `1px solid ${T.hairline}`,
          borderRadius: 22,
          padding: "24px 22px",
        }}
      >
        <Eyebrow>Daily intake</Eyebrow>
        <div
          style={{
            marginTop: 6,
            fontSize: "2.6rem",
            fontWeight: 700,
            letterSpacing: "-0.025em",
            lineHeight: 1,
            ...T.tabular,
            background: T.ctaGrad,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {dailyTarget ? dailyTarget.toLocaleString() : "—"}
          <span
            style={{
              fontSize: "0.9rem",
              color: T.muted,
              fontWeight: 700,
              marginLeft: 8,
              background: "none",
              WebkitTextFillColor: T.muted,
            }}
          >
            kcal
          </span>
        </div>
        {dailyTarget > 0 && dailyTarget < ((draft.sex ?? "X") === "F" ? 1200 : 1500) ? (
          <div style={{ marginTop: 8 }}>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "0.18rem 0.5rem",
              borderRadius: 999,
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "rgba(253,224,71,0.95)",
              border: "1px solid rgba(251,191,36,0.45)",
              letterSpacing: "0.02em",
            }}>Low intake</span>
          </div>
        ) : null}
        <div style={{ marginTop: 8, color: T.fg2, fontWeight: 600, fontSize: "0.84rem" }}>
          {endYmd
            ? `Hits ${displayWeight(draft.targetWeightLbs, draft.unitsWeight)} ${draft.unitsWeight} on ${new Date(endYmd + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
            : "Ongoing maintenance"}
        </div>

        <div style={{ position: "relative", marginTop: 18, height: 122 }}>
          <svg viewBox="0 0 320 110" width="100%" height="100" preserveAspectRatio="none" style={{ display: "block" }}>
            <defs>
              <linearGradient id="pg-preview-projection" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`M0,${y0} L316,${y1} L320,${y1} L320,110 L0,110 Z`} fill="url(#pg-preview-projection)" />
            <path d={`M0,${y0} L316,${y1}`} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
            <circle cx="0" cy={y0} r="4.5" fill="#fff" />
            <circle cx="316" cy={y1} r="5.5" fill="#84CC16" />
          </svg>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
              fontSize: "0.66rem",
              fontWeight: 700,
              color: T.muted,
              ...T.tabular,
              letterSpacing: "-0.005em",
            }}
          >
            <span>Today · {displayWeight(draft.currentWeightLbs, draft.unitsWeight)} {draft.unitsWeight}</span>
            <span style={{ color: "#a3e635" }}>
              {endYmd
                ? `${new Date(endYmd + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} · ${displayWeight(draft.targetWeightLbs, draft.unitsWeight)} ${draft.unitsWeight}`
                : `Holding · ${displayWeight(draft.currentWeightLbs, draft.unitsWeight)} ${draft.unitsWeight}`}
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <StatTile
          label="Pace"
          value={`${goal === "maintain" ? "0.0" : `${sign}${draft.paceLbsPerWeekAbs.toFixed(1)}`}`}
          suffix="lb/wk"
        />
        <StatTile
          label="Net change"
          value={`${goal === "maintain" ? "0.0" : `${sign}${Math.abs(draft.targetWeightLbs - draft.currentWeightLbs).toFixed(1)}`}`}
          suffix="lb"
        />
        <StatTile label="TDEE" value={tdee ? Math.round(tdee).toLocaleString() : "—"} suffix="kcal" />
      </div>
      <div style={{ color: T.muted, fontSize: "0.72rem", textAlign: "center", lineHeight: 1.5 }}>
        Resting burn ≈ {bmr ? Math.round(bmr).toLocaleString() : "—"} kcal/day
      </div>
    </>
  );
}

export function ActivityFactorLabel({ activityFactor }: { activityFactor: number }): React.ReactElement {
  const m = ACTIVITY_OPTIONS.find((o) => Math.abs(o.factor - activityFactor) < 0.001);
  return <>{m ? `${m.label} (×${m.factor.toFixed(2)})` : `×${activityFactor.toFixed(2)}`}</>;
}
